import { backdropClasses } from '@ahamove/web-components/lib/Backdrop';
import Button from '@ahamove/web-components/lib/Button';
import Dialog, { dialogClasses } from '@ahamove/web-components/lib/Dialog';
import DialogContent from '@ahamove/web-components/lib/DialogContent';
import Stack from '@ahamove/web-components/lib/Stack';
import Typography from '@ahamove/web-components/lib/Typography';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react';
import type { User } from '@/api/auth/types';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import { useWebInAppStore } from '@/global-stores/useWebInAppStore';
import type { WebInAppID } from '@/lib/constants';
import { REQUIRED_AUTH_ROUTES } from '@/lib/constants';
import { isServer } from '@/lib/isServer';
import {
  ACCESS_TOKEN_KEY,
  getAccessToken,
  getRefreshToken,
  removeAccessToken,
  removeRefreshToken,
  storeAccessToken,
} from '@/lib/token';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { systemConfig } from '../system/systemConfig';

export interface AuthContextProps {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  signIn: (redirectTo: string) => void;
  logOut: () => void;
  openRequiredAuthDialog: (newValue: boolean) => void;
  user?: User;
  isLoadingUser: boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  signIn: () => {
    //
  },
  logOut: () => {
    //
  },
  openRequiredAuthDialog: () => {
    //
  },
  isLoadingUser: false,
});

export const useAuth = () => useContext(AuthContext);

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation(systemConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const { query, pathname, replace } = useRouter();
  const tokenParam = query[ACCESS_TOKEN_KEY] as string;
  const [accessToken, setAccessToken] = React.useState<
    AuthContextProps['accessToken']
  >(getAccessToken());
  const [refreshToken, setRefreshToken] = React.useState<
    AuthContextProps['refreshToken']
  >(getRefreshToken());
  const isAuthenticated = React.useMemo(() => !!accessToken, [accessToken]);
  const [openRequiredAuth, setOpenRequiredAuth] =
    React.useState<boolean>(false);

  const openRequiredAuthDialog = (newValue: boolean) => {
    console.log('newValue', newValue);
    setOpenRequiredAuth(newValue);
  };

  const { setWebInApp } = useWebInAppStore();

  const {
    data: user,
    isFetching: isLoadingUser,
    remove,
    isFetched,
  } = useGetUserProfile({
    enabled: isAuthenticated,
    queryKey: [isAuthenticated],
  });

  const signIn = React.useCallback((redirectTo: string) => {
    if (!isServer) {
      const { origin, pathname, search } = window.location;
      let redirectUrl = redirectTo || `${origin}${pathname}${search}`;
      if (window.encodeURIComponent)
        redirectUrl = window.encodeURIComponent(redirectUrl);

      window.open(
        `${process.env.NEXT_PUBLIC_AUTH_URI}?redirect_url=${redirectUrl}`,
        '_self'
      );
    }
  }, []);

  const logOut = React.useCallback(() => {
    removeAccessToken();
    removeRefreshToken();
    setAccessToken(null);
    setRefreshToken(null);
    remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const { webinapp } = query;

    if (webinapp) {
      setWebInApp(webinapp as WebInAppID);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  React.useEffect(() => {
    if (tokenParam) {
      removeAccessToken();
      storeAccessToken(tokenParam);
      setAccessToken(tokenParam);

      delete query[ACCESS_TOKEN_KEY];
      replace({ pathname: pathname, query: query }, undefined, {
        shallow: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenParam]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        refreshToken,
        signIn,
        logOut,
        openRequiredAuthDialog,
        user,
        isLoadingUser,
      }}
    >
      {children}

      {(accessToken &&
        !user &&
        isFetched &&
        REQUIRED_AUTH_ROUTES.includes(pathname)) ||
      openRequiredAuth ? (
        <Dialog
          fullScreen={isMobile}
          fullWidth
          maxWidth="xs"
          open
          sx={{
            [`.${backdropClasses.root}`]: {
              bgcolor: 'rgba(0, 0, 0, 0.35)',
            },
            [`.${dialogClasses.paper}`]: {
              m: isMobile ? 0 : 2,
              borderRadius: isMobile ? 0 : 3,
              boxShadow: isMobile
                ? 'none'
                : '9px 27px 32px rgba(8, 26, 61, 0.1)',
            },
          }}
        >
          <DialogContent sx={{ px: isMobile ? 2 : 3, pt: 3, pb: 2 }}>
            <Stack
              direction="column"
              spacing={3}
              justifyContent="center"
              height="100%"
            >
              <div className="relative mx-auto aspect-[19/10] h-auto w-full max-w-sm">
                <Image
                  src={'/static/images/required-auth.png'}
                  alt=""
                  layout="fill"
                />
              </div>
              <Typography variant="h3" color="grey.900" textAlign="center">
                {t('common:expire_token.title')}
              </Typography>
              <Typography
                variant="body2"
                color="grey.500"
                textAlign="center"
                sx={(theme) => ({ mt: `${theme.spacing(1)} !important` })}
              >
                {t('common:expire_token.des')}
              </Typography>
              <Button
                color="primary"
                size="large"
                fullWidth
                rounded
                onClick={() => signIn(`${window.location}`)}
                data-test-id="signInBtn"
              >
                {t('common:header.login')}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      ) : null}
    </AuthContext.Provider>
  );
};
